import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider, useWatch, type SubmitErrorHandler } from 'react-hook-form';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Save, AlertCircle } from 'lucide-react';
import { inventoryItemSchema, type InventoryItem } from '../../types/inventory';
import { inventoryService } from '../../services/inventoryService';
import { greenBeanService } from '../../services/greenBeanService';
import { useSettingsStore } from '../../store/settingsStore';
import { Modal } from '../common/Modal';
import { useWeightUnit } from '../../hooks/useWeightUnit';
import type { GreenBeanProfile } from '../../types/greenBean';
import { useLanguage } from '../../features/language/useLanguage';
import {
  PROCESS_TYPES,
  FIELD_LABEL_KEYS,
  getNestedError,
  createEmptyGreenBeanProfile,
  type ProcessType,
  type InventoryModalFormData,
} from './inventoryModal/types';
import { BasicInformationSection } from './inventoryModal/BasicInformationSection';
import { GreenBeanExtrinsicSection } from './inventoryModal/GreenBeanExtrinsicSection';
import { StockCostSection } from './inventoryModal/StockCostSection';
import { SellingPackagingSection } from './inventoryModal/SellingPackagingSection';
import { ErrorSummaryModal } from './inventoryModal/ErrorSummaryModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (item: InventoryItem) => void;
  initialName?: string;
  itemToEdit?: InventoryItem;
  hideStockCostSection?: boolean;
  readOnly?: boolean;
}

const toKnownProcessType = (value?: string): ProcessType | '' => {
  if (!value) return '';
  const normalized = value.trim().toLowerCase();
  const matched = PROCESS_TYPES.find((type) => type.toLowerCase() === normalized);
  return (matched as ProcessType | undefined) ?? '';
};

export const CreateInventoryModal: React.FC<Props> = ({ isOpen, onClose, onSuccess, initialName, itemToEdit, hideStockCostSection = false, readOnly = false }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { defaultUnit } = useSettingsStore();
  const { unit, toggleUnit } = useWeightUnit(defaultUnit as 'kg' | 'gram');
  const [error, setError] = useState<string | null>(null);
  const [inactiveBlockedOpen, setInactiveBlockedOpen] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [errorModalOpen, setErrorModalOpen] = useState(false);

  const methods = useForm<InventoryModalFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(inventoryItemSchema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      name: initialName || '',
      status: 'active',
      reorderPoint: 0,
      standardCost: 0,
      itemType: 'green_bean',
      quantity: 0,
      unitOfMeasure: 'kg',
      green_bean_profile: createEmptyGreenBeanProfile(),
      enableProducer: false,
      enableSpecies: false,
      enableVariety: false,
      enableProcessType: false,
      processTypeSelection: '',
      processTypeOther: '',
    },
  });

  const { handleSubmit, setValue, reset, control, formState: { errors, isSubmitting, isDirty, isValid } } = methods;
  const itemType = useWatch({ control, name: 'itemType' });
  const unitOfMeasure = useWatch({ control, name: 'unitOfMeasure' });
  const enableProducer = useWatch({ control, name: 'enableProducer' });
  const enableVariety = useWatch({ control, name: 'enableVariety' });
  const processTypeSelection = useWatch({ control, name: 'processTypeSelection' });
  const processTypeOther = useWatch({ control, name: 'processTypeOther' });
  const speciesValue = useWatch({ control, name: 'green_bean_profile.species' });
  const isFinishedGood = itemType === 'finished_good';
  const isPcs = unitOfMeasure === 'pcs';
  const isGreenBean = itemType === 'green_bean';
  const hasValidationErrors = Object.keys(errors).length > 0;
  const formRef = useRef<HTMLFormElement>(null);

  const fieldErrorList = useMemo(() => {
    return FIELD_LABEL_KEYS
      .map(({ key, labelKey }) => {
        const err = getNestedError(errors, key);
        if (!err?.message) return null;
        return { key, label: t(labelKey), message: err.message };
      })
      .filter((entry): entry is { key: string; label: string; message: string } => entry !== null);
  }, [errors, t]);

  const focusField = (key: string) => {
    const el = formRef.current?.querySelector<HTMLElement>(`[data-field="${CSS.escape(key)}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const focusable = el.querySelector<HTMLElement>(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])'
    );
    focusable?.focus({ preventScroll: true });
  };

  const { data: allProfilesData } = useQuery({
    queryKey: ['green-beans'],
    queryFn: greenBeanService.getAll,
    staleTime: 5 * 60 * 1000,
    enabled: isOpen && isGreenBean,
  });

  const allProfiles = useMemo(() => allProfilesData || [], [allProfilesData]);
  const uniqueOptions = useMemo(() => {
    const extract = (key: keyof GreenBeanProfile) =>
      Array.from(new Set(allProfiles.map((profile) => profile[key] as string).filter(Boolean))).sort();
    return {
      producers: extract('producer'),
      species: extract('species'),
      varieties: extract('variety'),
    };
  }, [allProfiles]);

  // State to track the active item being edited, persisting it during the closing animation
  // to prevent UI flicker (e.g. button label changing from "Saving..." to "Create Item")
  const [activeItem, setActiveItem] = useState(itemToEdit);

  if (itemToEdit && itemToEdit !== activeItem) {
    setActiveItem(itemToEdit);
  }
  if (isOpen && !itemToEdit && activeItem) {
    setActiveItem(undefined);
  }

  const isEditMode = Boolean(activeItem);
  const isRoastedBulkRestrictedEdit = isEditMode && activeItem?.itemType === 'roasted_bulk';

  useEffect(() => {
    if (isOpen) {
      if (itemToEdit) {
        const existingProfile = itemToEdit.greenBeanProfiles?.find((profile) => profile.is_default_profile) || itemToEdit.greenBeanProfiles?.[0];
        const existingProcessType = (existingProfile?.process_type || '').trim();
        const knownProcessType = toKnownProcessType(existingProcessType);
        const isKnownProcessType = knownProcessType !== '';
        const isOtherProcessType = existingProcessType ? !isKnownProcessType || knownProcessType === 'Other' : false;
        reset({
          name: itemToEdit.name,
          code: itemToEdit.code,
          description: itemToEdit.description,
          status: itemToEdit.status,
          itemType: itemToEdit.itemType,
          unitOfMeasure: itemToEdit.unitOfMeasure,
          location: itemToEdit.location,
          quantity: itemToEdit.stock,
          standardCost: itemToEdit.standardCost,
          reorderPoint: itemToEdit.reorderPoint,
          estimatedSellingPrice: itemToEdit.estimatedSellingPrice,
          packSizeGrams: itemToEdit.packSizeGrams,
          green_bean_profile: existingProfile
            ? {
                ...createEmptyGreenBeanProfile(),
                ...existingProfile,
                producer: existingProfile.producer || '',
                species: existingProfile.species || '',
                variety: existingProfile.variety || '',
                process_type: knownProcessType || existingProcessType || '',
              }
            : createEmptyGreenBeanProfile(),
          enableProducer: Boolean(existingProfile?.producer),
          enableSpecies: Boolean(existingProfile?.species),
          enableVariety: Boolean(existingProfile?.variety),
          enableProcessType: Boolean(existingProcessType),
          processTypeSelection: isOtherProcessType ? 'Other' : knownProcessType,
          processTypeOther: isOtherProcessType && existingProcessType !== 'Other' ? existingProcessType : '',
        });
      } else {
        reset({
          name: initialName || '',
          code: '',
          description: '',
          status: 'active',
          reorderPoint: hideStockCostSection ? 1 : 0,
          standardCost: 0,
          itemType: 'green_bean',
          quantity: 0,
          unitOfMeasure: 'kg',
          location: '',
          estimatedSellingPrice: undefined,
          packSizeGrams: undefined,
          green_bean_profile: createEmptyGreenBeanProfile(),
          enableProducer: false,
          enableSpecies: false,
          enableVariety: false,
          enableProcessType: false,
          processTypeSelection: '',
          processTypeOther: '',
        });
      }
    } else {
      reset({
        name: '',
        code: '',
        description: '',
        status: 'active',
        reorderPoint: 0,
        standardCost: 0,
        itemType: 'green_bean',
        quantity: 0,
        unitOfMeasure: 'kg',
        location: '',
        estimatedSellingPrice: undefined,
        packSizeGrams: undefined,
        green_bean_profile: createEmptyGreenBeanProfile(),
        enableProducer: false,
        enableSpecies: false,
        enableVariety: false,
        enableProcessType: false,
        processTypeSelection: '',
        processTypeOther: '',
      });
    }
  }, [isOpen, initialName, itemToEdit, reset, hideStockCostSection]);

  useEffect(() => {
    if (!enableProducer) {
      setValue('green_bean_profile.producer', '');
    }
  }, [enableProducer, setValue]);

  useEffect(() => {
    if (!enableVariety) {
      setValue('green_bean_profile.variety', '');
    }
  }, [enableVariety, setValue]);

  useEffect(() => {
    if (!speciesValue) {
      setValue('green_bean_profile.variety', '');
    }
  }, [speciesValue, setValue]);

  const onSubmit = async (data: InventoryModalFormData) => {
    setError(null);
    setHasAttemptedSubmit(false);
    try {
      const isProducerEnabled = data.enableProducer ?? enableProducer;
      const isVarietyEnabled = data.enableVariety ?? enableVariety;
      const selectedProcessType = data.processTypeSelection ?? processTypeSelection;
      const selectedProcessTypeOther = data.processTypeOther ?? processTypeOther;
      const {
        enableProducer: ignoredEnableProducer,
        enableSpecies: ignoredEnableSpecies,
        enableVariety: ignoredEnableVariety,
        enableProcessType: ignoredEnableProcessType,
        processTypeSelection: ignoredProcessTypeSelection,
        processTypeOther: ignoredProcessTypeOther,
        ...payload
      } = data;
      void ignoredEnableProducer;
      void ignoredEnableSpecies;
      void ignoredEnableVariety;
      void ignoredEnableProcessType;
      void ignoredProcessTypeSelection;
      void ignoredProcessTypeOther;
      if (hideStockCostSection && payload.itemType === 'green_bean' && (!payload.reorderPoint || payload.reorderPoint <= 0)) {
        payload.reorderPoint = 1;
      }
      delete payload.newGreenBeanProfile;
      delete payload.greenBeanProfileId;

      if (payload.itemType !== 'green_bean') {
        delete payload.green_bean_profile;
      } else {
        const toOptionalUuid = (value?: string) => {
          const trimmed = (value || '').trim();
          return trimmed ? trimmed : undefined;
        };
        const resolvedProcessType =
          selectedProcessType === 'Other'
            ? selectedProcessTypeOther.trim()
            : selectedProcessType;
        const producerValue = isProducerEnabled ? payload.green_bean_profile?.producer?.trim() || '' : '';
        const speciesValueInput = payload.green_bean_profile?.species?.trim() || '';
        const varietyValue = isVarietyEnabled ? payload.green_bean_profile?.variety?.trim() || '' : '';
        const hasExtrinsicInput = Boolean(producerValue || speciesValueInput || varietyValue || resolvedProcessType);

        if (!hasExtrinsicInput) {
          delete payload.green_bean_profile;
        } else {
          payload.green_bean_profile = {
            ...createEmptyGreenBeanProfile(),
            ...payload.green_bean_profile,
            producer: producerValue,
            species: speciesValueInput,
            variety: varietyValue,
            process_type: resolvedProcessType,
            lot_id: toOptionalUuid(payload.green_bean_profile?.lot_id),
            roast_id: toOptionalUuid(payload.green_bean_profile?.roast_id),
            inventory_item_id: toOptionalUuid(payload.green_bean_profile?.inventory_item_id),
            sample_id: toOptionalUuid(payload.green_bean_profile?.sample_id),
          };
        }
      }

      let resultItem: InventoryItem;

      if (itemToEdit) {
        if (payload.status === 'inactive' && payload.itemType === 'green_bean') {
          const lots = await inventoryService.getLots(itemToEdit.id);
          const hasActiveLots = lots.some((lot) => lot.remainingQuantity > 0);
          if (hasActiveLots) {
            setInactiveBlockedOpen(true);
            return;
          }
        }

        resultItem = await inventoryService.update(itemToEdit.id, payload);
      } else {
        resultItem = await inventoryService.create(payload);
      }

      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      onSuccess(resultItem);
      // Announce a genuine create (not an edit) so the onboarding tour can
      // advance off step 1 — reliable because it fires only on success, unlike
      // watching the modal close (which also happens on Cancel/backdrop).
      if (!itemToEdit) {
        window.dispatchEvent(
          new CustomEvent('qrema:inventory-item-created', { detail: resultItem }),
        );
      }
      setError(null);
      setInactiveBlockedOpen(false);
      onClose();
      reset();
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error).message ||
        'Unknown error';
      setError(t('inventory.modal.errorSave', { message: errorMessage }));
    }
  };

  const onInvalid: SubmitErrorHandler<InventoryModalFormData> = () => {
    setHasAttemptedSubmit(true);
    setError(null);
    setErrorModalOpen(true);
  };

  const handleErrorItemClick = (key: string) => {
    setErrorModalOpen(false);
    setTimeout(() => focusField(key), 250);
  };

  const handleModalClose = () => {
    setError(null);
    setHasAttemptedSubmit(false);
    setInactiveBlockedOpen(false);
    setErrorModalOpen(false);
    onClose();
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleModalClose}
        title={activeItem ? t('inventory.modal.titleEdit') : t('inventory.modal.titleCreate')}
        maxWidth="4xl"
      >
        <FormProvider {...methods}>
          <form ref={formRef} onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6" noValidate>
            {error && (
              <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50/80 p-4 shadow-sm">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
            )}

            <BasicInformationSection
              showStatus={Boolean(activeItem)}
              isRoastedBulkRestrictedEdit={isRoastedBulkRestrictedEdit}
              readOnly={readOnly}
            />

            {isGreenBean && !isRoastedBulkRestrictedEdit && (
              <GreenBeanExtrinsicSection readOnly={readOnly} uniqueOptions={uniqueOptions} />
            )}

            {!hideStockCostSection && !isRoastedBulkRestrictedEdit && (
              <StockCostSection
                unit={unit}
                toggleUnit={toggleUnit}
                isEditMode={isEditMode}
                isPcs={isPcs}
                isFinishedGood={isFinishedGood}
                showActiveItem={Boolean(activeItem)}
                readOnly={readOnly}
              />
            )}

            {isFinishedGood && !isRoastedBulkRestrictedEdit && (
              <SellingPackagingSection unit={unit} toggleUnit={toggleUnit} readOnly={readOnly} />
            )}

            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={handleModalClose}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                {readOnly ? t('common.actions.close') : t('auth.common.cancel')}
              </button>
              {!readOnly && (
                <button
                  type="submit"
                  data-test-id="inventory-create-submit"
                  disabled={isSubmitting || (Boolean(activeItem) && !isDirty) || (hasAttemptedSubmit && (!isValid || hasValidationErrors))}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {isSubmitting
                    ? activeItem
                      ? t('inventory.modal.actions.saving')
                      : t('inventory.modal.actions.creating')
                    : activeItem
                      ? t('inventory.modal.actions.saveChanges')
                      : t('inventory.modal.actions.createItem')}
                </button>
              )}
            </div>
          </form>
        </FormProvider>
      </Modal>

      <Modal
        isOpen={inactiveBlockedOpen}
        onClose={() => setInactiveBlockedOpen(false)}
        title={t('inventory.modal.warning.title')}
        maxWidth="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t('inventory.modal.warning.inactiveBlocked')}
          </p>
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => setInactiveBlockedOpen(false)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              {t('inventory.modal.warning.ok')}
            </button>
          </div>
        </div>
      </Modal>

      <ErrorSummaryModal
        isOpen={errorModalOpen}
        onClose={() => setErrorModalOpen(false)}
        fieldErrors={fieldErrorList}
        onItemClick={handleErrorItemClick}
      />
    </>
  );
};
